import React, { useCallback, useState } from 'react';
import { TrendingUp, Cpu, HardDrive, Activity } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { authenticatedFetch } from '../../utils/api';
import { API_HOST } from '../../utils/constants';
import { useSequentialPolling } from '../../hooks/useSequentialPolling';

/**
 * 系统监控面板
 * 显示 CPU、内存、任务队列等实时监控数据
 */
const SystemMonitor = ({ isOpen, onClose }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();

  const [metrics, setMetrics] = useState({
    queue: {
      queueSize: 0,
      activeThreads: 0,
      completedTasks: 0,
      poolSize: 0,
      estimatedWaitTime: 0,
    },
    system: {
      totalMemory: 0,
      freeMemory: 0,
      usedMemory: 0,
      maxMemory: 0,
      memoryUsagePercent: 0,
      availableProcessors: 0,
      systemLoadAverage: 0,
    },
  });

  const [history, setHistory] = useState({
    queueSize: [],
    activeThreads: [],
    memoryUsage: [],
    timestamps: [],
  });

  const fetchMetrics = useCallback(async ({ signal } = {}) => {
    try {
      const data = await authenticatedFetch(`${API_HOST}/api/metrics/status`, {
        signal,
        timeout: 45_000,
      });
      if (signal?.aborted) return;
      setMetrics(data);

      // 更新历史数据（保留最近 30 个数据点）
      setHistory((prev) => ({
        queueSize: [...prev.queueSize, data.queue.queueSize].slice(-30),
        activeThreads: [...prev.activeThreads, data.queue.activeThreads].slice(-30),
        memoryUsage: [...prev.memoryUsage, data.system.memoryUsagePercent].slice(-30),
        timestamps: [...prev.timestamps, new Date().toLocaleTimeString()].slice(-30),
      }));
    } catch (err) {
      if (signal?.aborted) return;
      console.error('[SystemMonitor] Failed to fetch metrics:', err);
    }
  }, []);

  useSequentialPolling(fetchMetrics, 2000, { enabled: isOpen });

  if (!isOpen) return null;

  const formatBytes = (bytes) => {
    const gb = bytes / (1024 * 1024 * 1024);
    return `${gb.toFixed(2)} GB`;
  };

  const getStatusColor = (percent) => {
    if (percent < 50) return theme.colors.success;
    if (percent < 80) return theme.colors.warning;
    return theme.colors.error;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center animate-in fade-in duration-150"
      style={{ backgroundColor: 'var(--overlay-medium)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg shadow-2xl animate-in fade-in zoom-in-95 duration-200"
        style={{ backgroundColor: theme.colors.bgPrimary }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题栏 */}
        <div
          className="px-6 py-4 flex items-center justify-between border-b"
          style={{ borderColor: theme.colors.borderSecondary }}
        >
          <div className="flex items-center gap-3">
            <Activity size={20} style={{ color: theme.colors.accentPrimary }} />
            <h2 className="text-lg font-bold" style={{ color: theme.colors.textPrimary }}>
              系统监控
            </h2>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded text-sm"
            style={{
              backgroundColor: 'var(--color-bg-tertiary)',
              color: theme.colors.textSecondary,
            }}
          >
            关闭
          </button>
        </div>

        {/* 内容区 */}
        <div className="p-6 space-y-6">
          {/* 任务队列状态 */}
          <div>
            <h3
              className="text-sm font-semibold mb-3 flex items-center gap-2"
              style={{ color: theme.colors.textPrimary }}
            >
              <TrendingUp size={16} />
              任务队列
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard
                label="处理中"
                value={`${metrics.queue.activeThreads} / ${metrics.queue.poolSize}`}
                color={theme.colors.accentPrimary}
                theme={theme}
              />
              <MetricCard
                label="排队中"
                value={metrics.queue.queueSize}
                color={getStatusColor((metrics.queue.queueSize / metrics.queue.poolSize) * 100)}
                theme={theme}
              />
              <MetricCard
                label="今日完成"
                value={metrics.queue.completedTasks}
                color={theme.colors.success}
                theme={theme}
              />
              <MetricCard
                label="预计等待"
                value={`${Math.floor(metrics.queue.estimatedWaitTime / 60)}分钟`}
                color={theme.colors.textSecondary}
                theme={theme}
              />
            </div>
          </div>

          {/* 系统资源 */}
          <div>
            <h3
              className="text-sm font-semibold mb-3 flex items-center gap-2"
              style={{ color: theme.colors.textPrimary }}
            >
              <Cpu size={16} />
              系统资源
            </h3>
            <div className="space-y-4">
              {/* 内存使用 */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs" style={{ color: theme.colors.textSecondary }}>
                    内存使用
                  </span>
                  <span className="text-xs font-medium" style={{ color: theme.colors.textPrimary }}>
                    {formatBytes(metrics.system.usedMemory)} / {formatBytes(metrics.system.maxMemory)}
                    <span style={{ color: getStatusColor(metrics.system.memoryUsagePercent) }}>
                      {' '}({metrics.system.memoryUsagePercent.toFixed(1)}%)
                    </span>
                  </span>
                </div>
                <div
                  className="h-2 rounded-full overflow-hidden"
                  style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
                >
                  <div
                    className="h-full transition-all duration-300"
                    style={{
                      width: `${metrics.system.memoryUsagePercent}%`,
                      backgroundColor: getStatusColor(metrics.system.memoryUsagePercent),
                    }}
                  />
                </div>
              </div>

              {/* CPU 信息 */}
              <div className="grid grid-cols-2 gap-4">
                <MetricCard
                  label="CPU 核心数"
                  value={metrics.system.availableProcessors}
                  color={theme.colors.textSecondary}
                  theme={theme}
                />
                <MetricCard
                  label="系统负载"
                  value={metrics.system.systemLoadAverage.toFixed(2)}
                  color={getStatusColor((metrics.system.systemLoadAverage / metrics.system.availableProcessors) * 100)}
                  theme={theme}
                />
              </div>
            </div>
          </div>

          {/* 历史趋势图（简化版） */}
          <div>
            <h3
              className="text-sm font-semibold mb-3 flex items-center gap-2"
              style={{ color: theme.colors.textPrimary }}
            >
              <HardDrive size={16} />
              实时趋势（最近 1 分钟）
            </h3>
            <div className="space-y-3">
              <MiniChart
                label="队列长度"
                data={history.queueSize}
                color={theme.colors.accentPrimary}
                theme={theme}
              />
              <MiniChart
                label="活跃线程"
                data={history.activeThreads}
                color={theme.colors.success}
                theme={theme}
              />
              <MiniChart
                label="内存使用 (%)"
                data={history.memoryUsage}
                color={theme.colors.warning}
                theme={theme}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 指标卡片组件
const MetricCard = ({ label, value, color, theme }) => (
  <div
    className="p-3 rounded-lg"
    style={{ backgroundColor: 'var(--color-bg-secondary)' }}
  >
    <div className="text-2xs mb-1" style={{ color: theme.colors.textMuted }}>
      {label}
    </div>
    <div className="font-mono-num text-lg font-bold" style={{ color }}>
      {value}
    </div>
  </div>
);

// 迷你图表组件
const MiniChart = ({ label, data, color, theme }) => {
  const max = Math.max(...data, 1);
  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * 100;
    const y = 100 - (value / max) * 100;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-2xs" style={{ color: theme.colors.textSecondary }}>
          {label}
        </span>
        <span className="text-2xs font-medium" style={{ color }}>
          {data[data.length - 1] || 0}
        </span>
      </div>
      <svg
        viewBox="0 0 100 30"
        className="w-full h-8"
        style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
      >
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
};

export default SystemMonitor;
